import React from 'react';

import PropTypes from 'prop-types';

import {
    Section,
    StarRating,
    Typography,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import { getRatingLabel } from '../../utils';
import OverviewSection from './OverviewSection/OverviewSection';
import UserReviews from './UserReviews/UserReviews';

import styles from './SimplifiedCarrierReviews.scss';

const propTypes = {
    carrierReviews: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.object),
        PropTypes.object,
    ]).isRequired,
    namespace: PropTypes.string.isRequired,
    reviewSummary: PropTypes.shape({
        overall: PropTypes.number.isRequired,
        reviewCount: PropTypes.number,
        travelType: PropTypes.number,
    }).isRequired,
};

const defaultProps = {
    ungrouped: false,
};

function SimplifiedCarrierReviews(props) {
    const {
        t,
        i18nNumber,
        carrierReviews,
        carrierStats = {},
        reviewSummary,
        namespace,
    } = props;

    if (carrierReviews === undefined) {
        return null;
    }

    const overallDescription = t(`${namespace}:travelReviewRouteDescription`);
    const ratingLabel = getRatingLabel(reviewSummary.overall);
    const localizedRatingLabel = i18nNumber(ratingLabel)
        .format('decimal', {
            maximumFractionDigits: 1,
            minimumFractionDigits: 1,
        });
    const ratingId = `${overallDescription}-${ratingLabel}`;
    const showCarrierStats = !!Object.keys(carrierStats).length;

    return (
        <Section id="carrier-review-section">
            <Typography
                className={styles.title}
                variant="h3"
            >
                {t(`${namespace}:travelReviewTitle`)}
            </Typography>
            <Typography display="inline">
                {t(`${namespace}:travelReviewRouteDescription`)}
            </Typography>
            <Typography display="inline">
                {` (${t(`${namespace}:reviewSummaryReviewCount`)})`}
            </Typography>
            <div className={styles.overallRating}>
                <StarRating
                    className={styles.starRating}
                    hoverEffects="none"
                    id={ratingId}
                    maxRating={1}
                    rating={1}
                    starClassNames={{
                        base: styles.stars,
                        isSelected: styles.starIsSelected,
                    }}
                />
                <Typography
                    component="span"
                    id={ratingId}
                    variant="h3"
                >
                    {localizedRatingLabel}
                </Typography>
            </div>
            {showCarrierStats && (
                <>
                    <Typography
                        className={styles.overviewSectionTitle}
                        variant="subtitle"
                    >
                        {t(`${namespace}:travelReviewCommonRatings`)}
                    </Typography>
                    <OverviewSection
                        namespace={namespace}
                        stats={carrierStats}
                    />
                </>
            )}
            <>
                {showCarrierStats && (
                    <Typography
                        className={styles.reviewSubtitle}
                        variant="h5"
                    >
                        {t(`${namespace}:travelReviewSubtitle`)}
                    </Typography>
                )}
                <UserReviews
                    defaultNumReviews={5}
                    endReviews={15}
                    namespace={namespace}
                    reviews={carrierReviews}
                    startReviews={5}
                />
            </>
        </Section>
    );
}

SimplifiedCarrierReviews.propTypes = propTypes;
SimplifiedCarrierReviews.defaultProps = defaultProps;

export { SimplifiedCarrierReviews };
export default localize(SimplifiedCarrierReviews);
